import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { MessageSquare } from 'lucide-react';
import { SupportChatProvider } from './SupportChatContext';
import { SupportChatWidget } from './SupportChatWidget';
import { useActiveConversation } from './useSupportChat';
import { SupportConversationStatus } from './types';
import { useSupportChatVisibility } from '@/contexts/SupportChatVisibilityContext';
import { useUserInitContactChannels } from '@/hooks/queries/useUserInitQuery';

interface SupportChatButtonProps {
  className?: string;
}

function SupportChatButtonContent({ className }: SupportChatButtonProps) {
  const contactChannels = useUserInitContactChannels();
  const hasChatEnabled = contactChannels.some((c) => c.channel_type === 'chat');

  const { isOpen, isMinimized, openChat, closeChat, minimizeChat } = useSupportChatVisibility();

  const { data: activeConversation, isLoading } = useActiveConversation();

  if (!hasChatEnabled) {
    return null;
  }

  // Only show button if we have a confirmed active conversation that is not closed
  // Check for null/undefined explicitly since API returns null when no active conversation
  const hasActiveConversation =
    !isLoading &&
    activeConversation != null &&
    activeConversation.id != null &&
    activeConversation.status !== SupportConversationStatus.CLOSED;
  const unreadCount = activeConversation?.unreadCount || 0;

  // Hide floating button when chat is open (like sidebar); show when chat is closed and there's an active conversation
  const showFloatingButton = !isOpen && hasActiveConversation;

  return (
    <>
      {/* Floating Chat Button - only shows when there's an active conversation */}
      {showFloatingButton && (
        <button
          onClick={openChat}
          className={cn(
            'fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-3 z-[260] md:bottom-4 md:right-4',
            'w-14 h-14 rounded-full',
            'bg-gradient-to-r from-blue-600 to-blue-700',
            'shadow-lg shadow-blue-500/25',
            'flex items-center justify-center',
            'hover:scale-105 active:scale-95 transition-transform',
            className
          )}
        >
          <MessageSquare className="h-6 w-6 text-white" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1.5 bg-red-500 text-white border-2 border-background"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </button>
      )}

      {/* Chat Widget */}
      <SupportChatWidget
        isOpen={isOpen && !isMinimized}
        onClose={closeChat}
        onMinimize={minimizeChat}
      />
    </>
  );
}

export function SupportChatButton(props: SupportChatButtonProps) {
  return (
    <SupportChatProvider>
      <SupportChatButtonContent {...props} />
    </SupportChatProvider>
  );
}
